<template>
    <section class="mt-10" :style="{ backgroundColor: bgColor }">
        <div class="px-4 py-8 mx-auto max-w-3xl md:py-16 xl:px-0">
            <h2 class="text-2xl text-center text-white font-ww-bold xl:text-42 md:text-3xl" v-html="mainTitle"></h2>

            <div class="mt-4 text-lg text-center text-white xl:mt-6 xl:text-26 md:text-xl font-ww-regular" v-html="subtitle"></div>

            <div class="flex flex-col gap-y-6 justify-center items-center px-5 mt-12 md:gap-y-10 md:flex-row lg:mb-8">
                <template v-for="(step, index) in steps" :key="index">
                    <div class="w-44 h-[212px] flex-shrink-0" :class="{ 'md:-ml-8': index > 0 }" :style="{ zIndex: steps.length - index }">
                        <div class="overflow-hidden relative h-full">
                            <div class="absolute inset-0">
                                <nuxt-img 
                                    :src="bgStepImage" 
                                    class="w-full h-full object-fit" 
                                    :alt="`Step ${index + 1} background`" 
                                    width="167"
                                    height="212"
                                    format="webp"
                                    loading="lazy"
                                />
                            </div>

                            <div class="flex relative z-10 flex-col h-full">
                                <div class="flex flex-1 justify-center items-center pt-6">
                                    <nuxt-img 
                                        :src="(step as any).imageSrc" 
                                        class="mx-auto" 
                                        :alt="(step as any).imageAlt" 
                                        :width="(step as any).imageWidth"
                                        :height="(step as any).imageHeight"
                                        format="webp"
                                        loading="lazy"
                                    />
                                </div>

                                <div class="flex justify-between items-center">
                                    <h3 class="pl-4 -mt-1 ml-1 text-lg text-white md:text-xl font-ww-bold" v-html="(step as any).title"></h3>

                                    <div class="flex-shrink-0" v-html="(step as any).numberSvg" aria-hidden="true"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="index < steps.length - 1" class="relative md:-ml-3 md-max:-mb-10 md-max:-mt-10" :style="{ zIndex: steps.length + 10 }" aria-hidden="true">
                        <svg class="w-20 md:w-24 lg:w-full sm-max:rotate-90" width="145" height="90" viewBox="0 0 145 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M52.4566 5.14551L90.4566 45.4435L52.4566 84.1455" stroke="#39434A" stroke-width="15" />
                            <path opacity="0.3" d="M96.4566 5.14551L134.457 45.4435L96.4566 84.1455" stroke="#39434A" stroke-width="15" />
                            <path opacity="0.3" d="M5.4566 5.14551L43.4566 45.4435L5.4566 84.1455" stroke="#39434A" stroke-width="15" />
                        </svg>
                    </div>
                </template>
            </div>

        </div>
    </section>
</template>

<script setup lang="ts">
const props = defineProps({
  bgColor: {
    type: String,
    default: '#07141D'
  },
  mainTitle: {
    type: String,
    default: 'Takes 10 seconds to make!'
  },
  subtitle: {
    type: String,
    default: 'Just Scoop, Pour, and Stir to <span class="underline text-lifeboost-green-400">Upgrade</span> Your Day'
  },
  bgStepImage: {
    type: String,
    default: '/assets/images/bg-step.png'
  },
  steps: {
    type: Array,
    default: () => [
      {
        imageSrc: '/assets/images/scoop.png',
        imageAlt: 'Scoop',
        imageWidth: 109,
        imageHeight: 74,
        title: 'Scoop',
        numberSvg: '<svg width="64" height="57" viewBox="0 0 64 57" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M36.8455 0L64 0V57H0L36.8455 0Z" fill="#C4E538" /><path d="M42.3054 24.0026H37.9989V21.0817C39.1723 21.0817 40.1085 20.9818 40.8075 20.7821C41.5315 20.5823 42.1057 20.2578 42.5301 19.8084C42.9545 19.3341 43.329 18.66 43.6536 17.7862H46.4248V44H42.3054V24.0026Z" fill="black" /></svg>'
      },
      {
        imageSrc: '/assets/images/pour.png',
        imageAlt: 'Pour',
        imageWidth: 116,
        imageHeight: 118,
        title: 'Pour',
        numberSvg: '<svg width="64" height="57" viewBox="0 0 64 57" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M36.8455 0L64 0V57H0L36.8455 0Z" fill="#C4E538" /><path d="M38.4635 44V41.1539C38.4635 37.4091 39.5121 35.1248 42.5828 31.9042C45.7285 28.5713 46.6272 26.5866 46.6272 24.1524C46.6272 21.7932 45.8034 21.1566 44.4927 21.1566C43.182 21.1566 42.3581 21.8681 42.3581 23.7405V26.5491H38.4635V24.0026C38.4635 19.8084 40.5606 17.4117 44.605 17.4117C48.6494 17.4117 50.7465 19.8084 50.7465 24.0026C50.7465 27.1108 49.4733 29.882 45.6536 33.9264C43.182 36.5478 42.5454 37.8585 42.5454 39.6934V40.2552H50.3721V44H38.4635Z" fill="black" /></svg>'
      },
      {
        imageSrc: '/assets/images/sfir.png',
        imageAlt: 'Stir',
        imageWidth: 96,
        imageHeight: 98,
        title: 'Stir',
        numberSvg: '<svg width="64" height="57" viewBox="0 0 64 57" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M36.8455 0L64 0V57H0L36.8455 0Z" fill="#C4E538"/><path d="M44.3803 44.3745C40.3359 44.3745 38.2388 42.0527 38.2388 37.8585V35.7239H42.1334V38.0457C42.1334 39.9181 42.9573 40.6297 44.268 40.6297C45.5787 40.6297 46.4025 39.993 46.4025 37.6712V35.5367C46.4025 33.1026 45.5787 32.2038 43.7063 32.2038H42.3207V28.459H43.8186C45.354 28.459 46.2902 27.7849 46.2902 25.6878V23.7779C46.2902 21.9055 45.4289 21.1566 44.2305 21.1566C43.0322 21.1566 42.2458 21.8681 42.2458 23.5907V25.3133H38.3512V23.9277C38.3512 19.7335 40.4483 17.4117 44.3803 17.4117C48.3124 17.4117 50.4095 19.6961 50.4095 23.8903V24.8265C50.4095 27.6351 49.5108 29.3577 47.4885 30.1067V30.1816C49.698 31.0054 50.5219 32.9153 50.5219 35.5741V37.8959C50.5219 42.0901 48.4248 44.3745 44.3803 44.3745Z" fill="black"/></svg>'
      }
    ]
  }
})

defineBuilderComponent({
  friendlyName: 'Info With Steps',
  inputs: [
    {
      name: 'bgColor',
      type: 'color',
      defaultValue: '#07141D'
    },
    {
      name: 'mainTitle',
      type: 'richText',
      defaultValue: 'Takes 10 seconds to make!'
    },
    {
      name: 'subtitle',
      type: 'richText',
      defaultValue: 'Just Scoop, Pour, and Stir to <span class="underline text-lifeboost-green-400">Upgrade</span> Your Day'
    },
    {
      name: 'bgStepImage',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/bg-step.png'
    },
    {
      name: 'steps',
      type: 'list',
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          required: true
        },
        {
          name: 'imageAlt',
          type: 'text',
          required: true
        },
        {
          name: 'imageWidth',
          type: 'number',
          defaultValue: 109
        },
        {
          name: 'imageHeight',
          type: 'number',
          defaultValue: 74
        },
        {
          name: 'title',
          type: 'text',
          required: true
        },
        {
          name: 'numberSvg',
          type: 'longText',
          helperText: 'SVG code for step number badge'
        }
      ],
      defaultValue: [
        {
          imageSrc: '/assets/images/scoop.png',
          imageAlt: 'Scoop',
          imageWidth: 109,
          imageHeight: 74,
          title: 'Scoop',
          numberSvg: '<svg width="64" height="57" viewBox="0 0 64 57" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M36.8455 0L64 0V57H0L36.8455 0Z" fill="#C4E538" /><path d="M42.3054 24.0026H37.9989V21.0817C39.1723 21.0817 40.1085 20.9818 40.8075 20.7821C41.5315 20.5823 42.1057 20.2578 42.5301 19.8084C42.9545 19.3341 43.329 18.66 43.6536 17.7862H46.4248V44H42.3054V24.0026Z" fill="black" /></svg>'
        },
        {
          imageSrc: '/assets/images/pour.png',
          imageAlt: 'Pour',
          imageWidth: 116,
          imageHeight: 118,
          title: 'Pour',
          numberSvg: '<svg width="64" height="57" viewBox="0 0 64 57" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M36.8455 0L64 0V57H0L36.8455 0Z" fill="#C4E538" /><path d="M38.4635 44V41.1539C38.4635 37.4091 39.5121 35.1248 42.5828 31.9042C45.7285 28.5713 46.6272 26.5866 46.6272 24.1524C46.6272 21.7932 45.8034 21.1566 44.4927 21.1566C43.182 21.1566 42.3581 21.8681 42.3581 23.7405V26.5491H38.4635V24.0026C38.4635 19.8084 40.5606 17.4117 44.605 17.4117C48.6494 17.4117 50.7465 19.8084 50.7465 24.0026C50.7465 27.1108 49.4733 29.882 45.6536 33.9264C43.182 36.5478 42.5454 37.8585 42.5454 39.6934V40.2552H50.3721V44H38.4635Z" fill="black" /></svg>'
        },
        {
          imageSrc: '/assets/images/sfir.png',
          imageAlt: 'Stir',
          imageWidth: 96,
          imageHeight: 98,
          title: 'Stir',
          numberSvg: '<svg width="64" height="57" viewBox="0 0 64 57" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M36.8455 0L64 0V57H0L36.8455 0Z" fill="#C4E538"/><path d="M44.3803 44.3745C40.3359 44.3745 38.2388 42.0527 38.2388 37.8585V35.7239H42.1334V38.0457C42.1334 39.9181 42.9573 40.6297 44.268 40.6297C45.5787 40.6297 46.4025 39.993 46.4025 37.6712V35.5367C46.4025 33.1026 45.5787 32.2038 43.7063 32.2038H42.3207V28.459H43.8186C45.354 28.459 46.2902 27.7849 46.2902 25.6878V23.7779C46.2902 21.9055 45.4289 21.1566 44.2305 21.1566C43.0322 21.1566 42.2458 21.8681 42.2458 23.5907V25.3133H38.3512V23.9277C38.3512 19.7335 40.4483 17.4117 44.3803 17.4117C48.3124 17.4117 50.4095 19.6961 50.4095 23.8903V24.8265C50.4095 27.6351 49.5108 29.3577 47.4885 30.1067V30.1816C49.698 31.0054 50.5219 32.9153 50.5219 35.5741V37.8959C50.5219 42.0901 48.4248 44.3745 44.3803 44.3745Z" fill="black"/></svg>'
        }
      ]
    }
  ]
})
</script>